/**
 * ControlBoothPage — the venue's live Home (`/`).
 *
 * ITEM 3 of the Master Control Center rollout (Feb 2026): the layout was
 * redesigned in /booth-preview and approved by the user. This file wires
 * that visual shell to LIVE data (MusicContext + backend). Nothing about
 * the data flow changed from the previous version — only presentation:
 *   1. Master Player   — currentTrack + transport (from MusicContext)
 *   2. Song List       — tracks in the currently selected day-playlist
 *   3. Request Inbox   — reuses <RequestsInbox />
 *   4. Vibe Picker     — GET /api/playlists/my-schedule filtered to today
 *   5. Search Bar      — GET /api/music/search + injectNext
 *   6. Celebrate hub   — GET /api/venue/celebrations → injectNext
 *
 * Design tokens: dark #09090B, zinc-900 cards, fuchsia-500 accent for the
 * Master Player / active states, pink-500 for the Celebrate hub, cyan-400
 * for the live-requests dot. Font stack: Outfit for titles, JetBrains
 * mono via `font-mono` for tracking-widest labels.
 *
 * IMPORTANT: All data-testid values from the previous version are
 * preserved so existing regression tests continue to pass.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Search, Play, Pause, SkipBack, SkipForward, Sparkles, Music2, Music,
  Loader2, Cake, PartyPopper, X, ListMusic, AudioLines, ChevronDown,
} from 'lucide-react';

import Layout from '../components/Layout';
import RequestsInbox from '../components/RequestsInbox';
import DeviceBadge from '../components/DeviceBadge';
import { useMusic } from '../contexts/MusicContext';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Weekday key that matches backend schedule shape (VALID_DAYS in server.py).
const _DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const todayKey = () => _DAY_KEYS[new Date().getDay()];

const fmtDuration = (ms) => {
  if (!ms || ms < 0) return '';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

// ─────────────────────────────────────────────────────────────────────────
// Master Player — desktop vertical / mobile horizontal card
// ─────────────────────────────────────────────────────────────────────────
const MasterPlayer = () => {
  const { currentTrack, isPlaying, progressMs, durationMs, play, pause, nextTrack, previousTrack, seekTo } = useMusic();
  const art = currentTrack?.album_image || currentTrack?.artwork_url || '';
  const name = currentTrack?.name || 'Nothing playing';
  const artist = Array.isArray(currentTrack?.artists) ? currentTrack.artists.join(', ') : (currentTrack?.artist_name || '');
  const album = currentTrack?.album_name || '';
  const pct = durationMs > 0 ? Math.min(100, (progressMs / durationMs) * 100) : 0;

  // Feb 14 2026 — draggable seek. While the user is dragging we render
  // from local `dragValue`; on release we commit to MusicKit via seekTo.
  // This avoids the thumb rubber-banding to the last emitted progressMs
  // between drag ticks and gives a modern audio-player feel.
  const [dragValue, setDragValue] = React.useState(null);
  const displayMs = dragValue != null ? dragValue : progressMs;
  const displayPct = durationMs > 0 ? Math.min(100, (displayMs / durationMs) * 100) : pct;
  const commitSeek = (ms) => {
    setDragValue(null);
    if (!durationMs) return;
    try { seekTo?.(Math.max(0, Math.min(ms, durationMs)) / 1000); } catch { /* noop */ }
  };

  return (
    <section
      className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col h-full min-h-0"
      data-testid="control-booth-master-player"
    >
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
        <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Master Player</span>
        <div className="flex items-center gap-2">
          <DeviceBadge compact />
          <span className="flex items-center gap-1 text-[10px] text-fuchsia-400 font-mono">
            <span className={`w-1.5 h-1.5 bg-fuchsia-500 rounded-full ${isPlaying ? 'animate-pulse' : ''}`} />
            {isPlaying ? 'LIVE' : 'IDLE'}
          </span>
        </div>
      </div>

      <div className="p-4 flex-1 flex flex-col min-h-0">
        {/* Art */}
        <div className="relative w-full aspect-square rounded-xl overflow-hidden border border-zinc-800 shadow-2xl flex-shrink-0">
          {art ? (
            <img src={art} alt={name} className="w-full h-full object-cover" data-testid="master-album-art" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-black">
              <Music2 className="w-14 h-14 text-zinc-700" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
        </div>

        {/* Meta */}
        <div className="mt-4 flex-shrink-0">
          <h2 className="text-xl font-black tracking-tight truncate" style={{ fontFamily: 'Outfit' }} data-testid="master-track-name">{name}</h2>
          <p className="text-zinc-400 text-sm truncate" data-testid="master-track-artist">
            {artist || '—'}{album ? ` · ${album}` : ''}
          </p>
        </div>

        {/* Progress — Feb 14 2026: draggable seek */}
        <div className="mt-3 flex-shrink-0">
          <div className="relative h-6 flex items-center group">
            <div className="absolute inset-x-0 h-1 bg-zinc-800 rounded-full overflow-hidden">
              <div className="h-full bg-fuchsia-500" style={{ width: `${displayPct}%` }} />
            </div>
            <input
              type="range"
              min={0}
              max={Math.max(1, durationMs || 0)}
              step={250}
              value={Math.min(displayMs, durationMs || 0)}
              onChange={(e) => setDragValue(Number(e.target.value))}
              onMouseUp={(e) => commitSeek(Number(e.currentTarget.value))}
              onTouchEnd={(e) => commitSeek(Number(e.currentTarget.value))}
              onKeyUp={(e) => commitSeek(Number(e.currentTarget.value))}
              disabled={!durationMs}
              aria-label="Seek within current track"
              data-testid="master-seekbar"
              className="absolute inset-x-0 w-full h-6 opacity-0 cursor-pointer disabled:cursor-not-allowed"
              style={{ WebkitAppearance: 'none' }}
            />
            <div
              className="absolute w-3 h-3 rounded-full bg-fuchsia-400 shadow-[0_0_10px_rgba(217,70,239,0.8)] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${displayPct}% - 6px)` }}
            />
          </div>
          <div className="flex justify-between text-[10px] font-mono text-zinc-500 mt-1">
            <span data-testid="master-progress-time">{fmtDuration(displayMs)}</span>
            <span data-testid="master-duration-time">{fmtDuration(durationMs)}</span>
          </div>
        </div>

        {/* Transport */}
        <div className="mt-4 flex items-center justify-center gap-3 flex-shrink-0">
          <button
            onClick={() => previousTrack?.()}
            className="w-11 h-11 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
            data-testid="master-prev"
            aria-label="Previous"
          >
            <SkipBack className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={() => (isPlaying ? pause?.() : play?.())}
            className="w-16 h-16 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 flex items-center justify-center shadow-[0_0_30px_rgba(217,70,239,0.5)] hover:scale-105 active:scale-95 transition-[transform,background-color]"
            data-testid="master-playpause"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying
              ? <Pause className="w-6 h-6 fill-current text-black" />
              : <Play className="w-7 h-7 fill-current text-black ml-0.5" />}
          </button>
          <button
            onClick={() => nextTrack?.()}
            className="w-11 h-11 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
            data-testid="master-next"
            aria-label="Next"
          >
            <SkipForward className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Compact mobile player — thin horizontal card
// ─────────────────────────────────────────────────────────────────────────
const MobilePlayerCompact = () => {
  const { currentTrack, isPlaying, progressMs, durationMs, play, pause, nextTrack, previousTrack } = useMusic();
  const art = currentTrack?.album_image || currentTrack?.artwork_url || '';
  const name = currentTrack?.name || 'Nothing playing';
  const artist = Array.isArray(currentTrack?.artists) ? currentTrack.artists.join(', ') : (currentTrack?.artist_name || '');
  const pct = durationMs > 0 ? Math.min(100, (progressMs / durationMs) * 100) : 0;

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-900 border border-zinc-800"
      data-testid="mobile-player-card"
    >
      <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-800">
        {art ? (
          <img src={art} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music2 className="w-5 h-5 text-zinc-600" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-white text-sm font-bold truncate" style={{ fontFamily: 'Outfit' }}>{name}</p>
        <div className="flex items-center gap-2">
          <p className="text-zinc-400 text-xs truncate flex-1 min-w-0">{artist || '—'}</p>
          <DeviceBadge compact />
        </div>
        <div className="mt-1 h-0.5 bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full bg-fuchsia-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => previousTrack?.()}
          className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Previous"
        >
          <SkipBack className="w-3.5 h-3.5 text-white" />
        </button>
        <button
          onClick={() => (isPlaying ? pause?.() : play?.())}
          className="w-11 h-11 rounded-full bg-fuchsia-500 flex items-center justify-center shadow-[0_0_16px_rgba(217,70,239,0.5)] active:scale-95 transition-transform"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause className="w-4 h-4 fill-current text-black" /> : <Play className="w-4 h-4 fill-current text-black ml-0.5" />}
        </button>
        <button
          onClick={() => nextTrack?.()}
          className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Next"
        >
          <SkipForward className="w-3.5 h-3.5 text-white" />
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Big centered mobile player — Feb 14 2026 (view-only home layout)
// Full-width square artwork, meta below, draggable seek, transport controls.
// Replaces click-to-jump on the tracklist as the primary playback surface.
// ─────────────────────────────────────────────────────────────────────────
const MobileBigPlayer = () => {
  const { currentTrack, isPlaying, progressMs, durationMs, play, pause, nextTrack, previousTrack, seekTo } = useMusic();
  const art = currentTrack?.album_image || currentTrack?.artwork_url || '';
  const name = currentTrack?.name || 'Nothing playing';
  const artist = Array.isArray(currentTrack?.artists) ? currentTrack.artists.join(', ') : (currentTrack?.artist_name || '');
  const pct = durationMs > 0 ? Math.min(100, (progressMs / durationMs) * 100) : 0;
  const [dragValue, setDragValue] = React.useState(null);
  const displayMs = dragValue != null ? dragValue : progressMs;
  const displayPct = durationMs > 0 ? Math.min(100, (displayMs / durationMs) * 100) : pct;
  const commitSeek = (ms) => {
    setDragValue(null);
    if (!durationMs) return;
    try { seekTo?.(Math.max(0, Math.min(ms, durationMs)) / 1000); } catch { /* noop */ }
  };
  return (
    <div className="flex flex-col items-center" data-testid="mobile-big-player">
      {/* Artwork */}
      <div className="relative w-full max-w-[320px] aspect-square rounded-3xl overflow-hidden border border-zinc-800 shadow-[0_0_60px_rgba(217,70,239,0.25)]">
        {art ? (
          <img src={art} alt={name} className="w-full h-full object-cover" data-testid="mobile-big-art" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-black">
            <Music2 className="w-20 h-20 text-zinc-700" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
      </div>

      {/* Meta */}
      <div className="mt-5 w-full text-center">
        <h2 className="text-xl font-black tracking-tight truncate px-4" style={{ fontFamily: 'Outfit' }} data-testid="mobile-big-name">
          {name}
        </h2>
        <p className="text-zinc-400 text-sm truncate px-4 mt-0.5" data-testid="mobile-big-artist">{artist || '—'}</p>
      </div>

      {/* Draggable seek */}
      <div className="mt-5 w-full">
        <div className="relative h-6 flex items-center">
          <div className="absolute inset-x-0 h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-fuchsia-500" style={{ width: `${displayPct}%` }} />
          </div>
          <input
            type="range"
            min={0}
            max={Math.max(1, durationMs || 0)}
            step={250}
            value={Math.min(displayMs, durationMs || 0)}
            onChange={(e) => setDragValue(Number(e.target.value))}
            onMouseUp={(e) => commitSeek(Number(e.currentTarget.value))}
            onTouchEnd={(e) => commitSeek(Number(e.currentTarget.value))}
            onKeyUp={(e) => commitSeek(Number(e.currentTarget.value))}
            disabled={!durationMs}
            aria-label="Seek within current track"
            data-testid="mobile-big-seek"
            className="absolute inset-x-0 w-full h-6 opacity-0 cursor-pointer disabled:cursor-not-allowed"
            style={{ WebkitAppearance: 'none' }}
          />
          <div
            className="absolute w-3.5 h-3.5 rounded-full bg-fuchsia-400 shadow-[0_0_12px_rgba(217,70,239,0.85)] pointer-events-none"
            style={{ left: `calc(${displayPct}% - 7px)` }}
          />
        </div>
        <div className="flex justify-between text-[10px] font-mono text-zinc-500 mt-1">
          <span>{fmtDuration(displayMs)}</span>
          <span>{fmtDuration(durationMs)}</span>
        </div>
      </div>

      {/* Transport */}
      <div className="mt-5 flex items-center justify-center gap-5">
        <button
          onClick={() => previousTrack?.()}
          className="w-12 h-12 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center active:scale-95 transition-transform"
          data-testid="mobile-big-prev"
          aria-label="Previous"
        >
          <SkipBack className="w-5 h-5 text-white" />
        </button>
        <button
          onClick={() => (isPlaying ? pause?.() : play?.())}
          className="w-20 h-20 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 flex items-center justify-center shadow-[0_0_36px_rgba(217,70,239,0.6)] active:scale-95 transition-transform"
          data-testid="mobile-big-playpause"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying
            ? <Pause className="w-8 h-8 fill-current text-black" />
            : <Play className="w-9 h-9 fill-current text-black ml-1" />}
        </button>
        <button
          onClick={() => nextTrack?.()}
          className="w-12 h-12 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center active:scale-95 transition-transform"
          data-testid="mobile-big-next"
          aria-label="Next"
        >
          <SkipForward className="w-5 h-5 text-white" />
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Search bar — debounced Apple search + one-tap queue
// ─────────────────────────────────────────────────────────────────────────
const BoothSearchBar = ({ compact = false }) => {
  const { injectNext } = useMusic();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await axios.get(`${API}/music/search`, { params: { q: q.trim(), limit: 8 } });
        setResults(r.data?.tracks || []);
      } catch { setResults([]); }
      finally { setBusy(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const addToQueue = async (t) => {
    setAddingId(t.id);
    try {
      await injectNext(t);
      toast.success(`Queued: ${t.name}`);
      setQ(''); setResults([]);
    } catch { toast.error('Could not queue that track'); }
    finally { setAddingId(null); }
  };

  const inputH = compact ? 'h-11' : 'h-14';
  const inputPad = compact ? 'pl-11 text-sm' : 'pl-14 text-base';
  const iconPos = compact ? 'left-4 w-4 h-4' : 'left-5 w-5 h-5';

  return (
    <div className="relative flex-1" data-testid="control-booth-search">
      <Search className={`absolute top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none ${iconPos}`} />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={compact ? 'Add a song to the queue…' : 'Search or paste a link to queue a track…'}
        className={`w-full ${inputH} ${inputPad} pr-10 bg-zinc-900 border border-zinc-800 rounded-full text-white outline-none focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-500/40 placeholder:text-zinc-600`}
        style={{ fontFamily: 'Outfit' }}
        data-testid="control-booth-search-input"
      />
      {busy && (
        <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 animate-spin" />
      )}
      <AnimatePresence>
        {results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 top-full mt-2 rounded-2xl bg-zinc-900 border border-zinc-800 overflow-hidden z-30 max-h-[60vh] overflow-y-auto shadow-2xl"
            data-testid="control-booth-search-results"
          >
            {results.map((t) => (
              <button
                key={t.id}
                onClick={() => addToQueue(t)}
                disabled={!!addingId}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-left disabled:opacity-50"
                data-testid={`search-add-${t.id}`}
              >
                {t.album_image ? (
                  <img src={t.album_image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded bg-zinc-800 flex items-center justify-center flex-shrink-0">
                    <Music2 className="w-4 h-4 text-zinc-600" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium truncate">{t.name}</p>
                  <p className="text-zinc-500 text-xs truncate">
                    {(Array.isArray(t.artists) ? t.artists.join(', ') : t.artist_name) || ''}
                  </p>
                </div>
                {addingId === t.id ? (
                  <Loader2 className="w-4 h-4 text-fuchsia-400 animate-spin" />
                ) : (
                  <span className="text-[10px] uppercase tracking-widest font-mono text-fuchsia-400">Queue</span>
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Playlist state — shared across Picker + SongList
// ─────────────────────────────────────────────────────────────────────────
const usePlaylistState = () => {
  const { playTracks, injectNext, queue: liveQueue, queueIndex: liveQueueIndex } = useMusic();
  const [schedule, setSchedule] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tracksLoading, setTracksLoading] = useState(false);
  const [autoPlayOnLoad, setAutoPlayOnLoad] = useState(false);
  // Live-playing playlist metadata (set by the queue-current poll below).
  // Feb 2026 — the Home Booth's playlist column follows what's ACTUALLY
  // playing rather than today's schedule, so the DJ can see where the
  // queue is in real time even if it's playing a non-scheduled playlist.
  // We also cache the queue's TRACKS + CURRENT_INDEX from the backend so
  // the column is populated the moment the page loads (before the user
  // taps Play and before MusicContext's local `queue` hydrates).
  const [live, setLive] = useState({ id: null, name: null, type: null, tracks: [], currentIndex: 0 });

  const todayPlaylists = useMemo(() => {
    const day = todayKey();
    const slots = schedule?.[day] || {};
    const out = [];
    const seen = new Set();
    Object.entries(slots).forEach(([slot, pl]) => {
      if (pl && pl.id && !seen.has(pl.id)) {
        seen.add(pl.id);
        out.push({ ...pl, _slot: slot });
      }
    });
    return out;
  }, [schedule]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/playlists/my-schedule`);
        if (!mounted) return;
        setSchedule(r.data?.schedule || {});
      } catch { /* handled in render */ }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!selectedId && todayPlaylists.length > 0) setSelectedId(todayPlaylists[0].id);
  }, [todayPlaylists, selectedId]);

  // Compute the resume index for a given playlist by probing the backend
  // queue. Feb 2026 — mirrors the DJ Console fix so the Home Booth
  // resumes from song N after the owner reloads/reopens the tab and taps
  // the same playlist card, instead of restarting from track 1.
  const resumeIndexForPlaylist = useCallback(async (playlistId, trackCount) => {
    if (!playlistId || !trackCount) return 0;
    try {
      const q = await axios.get(`${API}/queue/current`);
      if (q.data?.playlist_id === playlistId) {
        const idx = Math.max(0, Math.min(q.data?.current_index || 0, trackCount - 1));
        return idx;
      }
    } catch (_) { /* fall through to fresh start */ }
    return 0;
  }, []);

  useEffect(() => {
    if (!selectedId) { setTracks([]); return; }
    let mounted = true;
    (async () => {
      setTracksLoading(true);
      try {
        const r = await axios.get(`${API}/playlists/curated/${selectedId}`);
        if (!mounted) return;
        const list = r.data?.tracks || [];
        setTracks(list);
        if (autoPlayOnLoad && list.length > 0) {
          setAutoPlayOnLoad(false);
          const startIdx = await resumeIndexForPlaylist(selectedId, list.length);
          try {
            await playTracks(list, startIdx, {
              playlistId: selectedId,
              playlistType: 'curated',
            });
            if (startIdx > 0) toast.success(`Resuming from track ${startIdx + 1}`);
          } catch { /* toast in playTracks */ }
        }
      } catch { if (mounted) setTracks([]); }
      finally { if (mounted) setTracksLoading(false); }
    })();
    return () => { mounted = false; };
  }, [selectedId, autoPlayOnLoad, playTracks, resumeIndexForPlaylist]);

  const pickPlaylist = useCallback(async (plId) => {
    setAutoPlayOnLoad(true);
    if (plId === selectedId && tracks.length > 0) {
      // Same playlist re-tapped and we already have its tracks locally
      // — kick off with resume-aware index without waiting on selectedId
      // change.
      const startIdx = await resumeIndexForPlaylist(plId, tracks.length);
      playTracks(tracks, startIdx, {
        playlistId: plId,
        playlistType: 'curated',
      }).catch(() => {});
      if (startIdx > 0) toast.success(`Resuming from track ${startIdx + 1}`);
    } else {
      setSelectedId(plId);
    }
  }, [selectedId, tracks, playTracks, resumeIndexForPlaylist]);

  const playFromIndex = useCallback(async (idx) => {
    // When we're live-playing, tapping a row should re-arm from THAT index
    // of the live queue — not the (potentially stale) scheduled tracks.
    const list = (liveQueue?.length || 0) > 0 ? liveQueue : tracks;
    if (!list.length) return;
    try {
      await playTracks(list, idx, {
        playlistId: (live.id || selectedId) || null,
        playlistType: (live.type || 'curated'),
      });
    }
    catch (e) { toast.error('Could not start playback'); console.warn('[Booth] playTracks failed:', e?.message); }
  }, [tracks, liveQueue, playTracks, selectedId, live]);

  const queueTrack = useCallback(async (t) => {
    try { await injectNext(t); toast.success(`Queued: ${t.name}`); }
    catch { toast.error('Could not queue that track'); }
  }, [injectNext]);

  // Poll /queue/current every 4s so the "Now Spinning" header reflects
  // whatever's actually playing — including a playlist that isn't in
  // today's schedule (e.g. the DJ picked one from Playlists). Cheap
  // read, cached in the SW.
  useEffect(() => {
    let mounted = true;
    let timer;
    const probe = async () => {
      try {
        const q = await axios.get(`${API}/queue/current`);
        if (!mounted) return;
        setLive({
          id: q.data?.playlist_id || null,
          name: q.data?.playlist_name || null,
          type: q.data?.playlist_type || null,
          tracks: q.data?.tracks || [],
          currentIndex: q.data?.current_index || 0,
        });
      } catch { /* transient — retry on next tick */ }
    };
    probe();
    timer = setInterval(probe, 4000);
    return () => { mounted = false; if (timer) clearInterval(timer); };
  }, []);

  // Effective playlist = whatever is LIVE-playing (from local queue OR the
  // backend snapshot), else today's schedule pick. Prefer the local
  // in-memory queue when it has content because the DJ may have mutated
  // it (queueTrack, remove) before those mutations synced back to the
  // server.
  const scheduleSelected = todayPlaylists.find((p) => p.id === selectedId);
  const localHasQueue = (liveQueue?.length || 0) > 0;
  const backendHasQueue = (live.tracks?.length || 0) > 0;
  const isLive = !!live.id && (localHasQueue || backendHasQueue);
  const effectiveSelected = isLive
    ? { id: live.id, name: live.name || scheduleSelected?.name || 'Live queue', _live: true }
    : scheduleSelected;
  const effectiveTracks = localHasQueue ? liveQueue : (backendHasQueue ? live.tracks : tracks);
  // If we're rendering the backend snapshot (no local playback yet), use
  // its index. Once local playback starts, MusicContext's queueIndex
  // takes over — the SongList prop wiring picks that up.
  const effectiveActiveIndex = localHasQueue ? liveQueueIndex : (backendHasQueue ? live.currentIndex : null);

  return {
    todayPlaylists,
    selectedId,
    tracks: effectiveTracks,
    selected: effectiveSelected,
    loading, tracksLoading,
    liveQueueIndex: effectiveActiveIndex,
    isLive,
    pickPlaylist, playFromIndex, queueTrack,
  };
};

// ─────────────────────────────────────────────────────────────────────────
// Song List — the tracks inside the currently selected playlist
// ─────────────────────────────────────────────────────────────────────────
const SongList = ({
  selected, tracks, loading, tracksLoading,
  todayPlaylistsCount, onQueue,
  currentTrackId, activeIndex, isLive,
  collapsible = false, defaultCollapsed = false,
}) => {
  // Auto-scroll the currently-playing row into view whenever the active
  // index changes. Uses a nearest-scroll so the DJ doesn't lose their
  // manually-scrolled position unless the row is off-screen.
  const listRef = React.useRef(null);
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
  React.useEffect(() => {
    if (collapsed) return;
    if (activeIndex == null || activeIndex < 0) return;
    const node = listRef.current?.querySelector(`[data-testid="playlist-row-${activeIndex}"]`);
    if (node) {
      try { node.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch { /* noop */ }
    }
  }, [activeIndex, collapsed]);

  // Feb 14 2026 — SongList is READ-ONLY on the Home booth.
  //
  // Field-report chain: host clicks track 7 while track 2 is playing →
  // playTracks' same-queue fast-path collides with an in-flight crossfade
  // / prewarm → Apple MusicKit silently no-ops the jump → UI vs audio
  // divergence + repeat loops. Rather than keep patching MusicKit's
  // race conditions, we remove the trigger surface entirely on the Home
  // screen — rows are informational only. Queue-after-current (`onQueue`)
  // is preserved because it's a queue MUTATION, not a mid-playback jump,
  // and doesn't race with the crossfade pipeline.

  return (
  <section
    className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col h-full min-h-0"
    data-testid="control-booth-playlist-tracks"
  >
    <button
      type="button"
      onClick={() => { if (collapsible) setCollapsed((v) => !v); }}
      className={`w-full px-5 py-3 border-b border-zinc-800 flex items-center justify-between flex-shrink-0 text-left ${collapsible ? 'hover:bg-zinc-800/40 transition-colors' : 'cursor-default'}`}
      data-testid="playlist-tracks-toggle"
      aria-expanded={!collapsed}
    >
      <div className="min-w-0 flex-1">
        <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono inline-flex items-center gap-2">
          Now Spinning
          {isLive && (
            <span className="inline-flex items-center gap-1 text-fuchsia-400" data-testid="playlist-live-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500 animate-pulse" />
              LIVE
            </span>
          )}
        </span>
        <h3 className="text-lg font-bold truncate" style={{ fontFamily: 'Outfit' }}>
          {selected ? selected.name : 'Playlist tracks'}
          {selected && tracks.length > 0 && (
            <span className="text-zinc-500 font-mono text-sm" data-testid="playlist-track-count">
              {' · '}
              {activeIndex != null && activeIndex >= 0
                ? `${activeIndex + 1} / ${tracks.length}`
                : `${tracks.length} tracks`}
            </span>
          )}
        </h3>
      </div>
      {collapsible ? (
        <ChevronDown className={`w-4 h-4 text-zinc-400 flex-shrink-0 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
      ) : (
        <ListMusic className="w-4 h-4 text-zinc-500 flex-shrink-0" />
      )}
    </button>
    {!collapsed && (
    <div ref={listRef} className="flex-1 overflow-y-auto min-h-0">
      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 text-zinc-500 animate-spin" /></div>
      ) : todayPlaylistsCount === 0 && !isLive ? (
        <div className="p-6 text-center text-zinc-500 text-sm">
          No playlists scheduled for today. Set them up in{' '}
          <span className="text-fuchsia-400">Playlists → Schedule</span>.
        </div>
      ) : tracksLoading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 text-zinc-500 animate-spin" /></div>
      ) : tracks.length === 0 ? (
        <div className="p-6 text-center text-zinc-500 text-sm">This playlist is empty.</div>
      ) : (
        <ul>
          {tracks.map((t, idx) => {
            // Prefer positional match (accurate even when a playlist
            // has duplicate track IDs); fall back to ID match when the
            // live queue is empty (schedule-view mode).
            const active = (activeIndex != null && activeIndex === idx) ||
                           (activeIndex == null && currentTrackId && t.id === currentTrackId);
            return (
              <li
                key={t.id ? `${t.id}-${idx}` : idx}
                className={`group flex items-center gap-4 px-5 py-3 border-b border-zinc-800/50 ${
                  active ? 'bg-fuchsia-500/5 border-l-4 border-l-fuchsia-500' : ''
                }`}
                data-testid={`playlist-row-${idx}`}
                aria-current={active ? 'true' : undefined}
              >
                <span className={`text-xs font-mono w-6 text-right ${active ? 'text-fuchsia-400' : 'text-zinc-500'}`}>
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${active ? 'text-fuchsia-400' : 'text-white'}`} style={{ fontFamily: 'Outfit' }}>
                    {t.name}
                  </p>
                  <p className="text-xs text-zinc-500 truncate">
                    {(Array.isArray(t.artists) ? t.artists.join(', ') : t.artist_name) || ''}
                  </p>
                </div>
                {active && <AudioLines className="w-4 h-4 text-fuchsia-400 animate-pulse flex-shrink-0" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
    )}
  </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Vibe Picker — horizontal image tiles
// ─────────────────────────────────────────────────────────────────────────
const VibePicker = ({ todayPlaylists, selectedId, onPick, size = 'lg' }) => {
  if (todayPlaylists.length === 0) return null;
  const w = size === 'lg' ? 'w-48 h-24' : 'w-40 h-20';
  return (
    <div className="flex flex-col" data-testid="control-booth-playlist-picker">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">
          Today's Vibes · {new Date().toLocaleDateString(undefined, { weekday: 'long' })}
        </span>
        <span className="text-[10px] text-zinc-600 font-mono hidden md:inline">← swipe →</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x scrollbar-thin">
        {todayPlaylists.map((pl) => {
          const active = pl.id === selectedId;
          return (
            <button
              key={pl.id}
              onClick={() => onPick(pl.id)}
              className={`relative flex-shrink-0 ${w} rounded-2xl overflow-hidden border transition-all hover:scale-[1.02] active:scale-95 snap-start ${
                active ? 'border-fuchsia-500 shadow-[0_0_20px_rgba(217,70,239,0.4)]' : 'border-zinc-800 hover:border-zinc-600'
              }`}
              data-testid={`picker-${pl.id}`}
            >
              {pl.image_url ? (
                <img src={pl.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-900/40 to-black" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
              <div className="absolute inset-0 p-3 flex flex-col justify-end text-left">
                <p className="text-white font-bold text-sm leading-tight line-clamp-1" style={{ fontFamily: 'Outfit' }}>{pl.name}</p>
                <div className="flex items-center gap-2">
                  <p className="text-zinc-400 text-[10px] font-mono uppercase">{pl._slot}</p>
                  {typeof pl.track_count === 'number' && (
                    <p className="text-zinc-500 text-[10px] font-mono">· {pl.track_count} tracks</p>
                  )}
                </div>
              </div>
              {active && (
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-fuchsia-500 text-black text-[9px] font-bold tracking-wider">LIVE</div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Celebration Modal
// ─────────────────────────────────────────────────────────────────────────
const CelebrationModal = ({ open, onClose }) => {
  const { injectNext } = useMusic();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [firingId, setFiringId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/venue/celebrations`);
        if (mounted) setTracks(r.data?.tracks || []);
      } catch { if (mounted) setTracks([]); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [open]);

  const fire = async (t) => {
    setFiringId(t.id);
    try {
      await injectNext(t);
      toast.success(`🎉 ${t.name} — plays next!`);
      onClose();
    } catch { toast.error('Could not queue the celebration'); }
    finally { setFiringId(null); }
  };

  const iconFor = (t) => {
    const n = (t?.name || '').toLowerCase();
    if (n.includes('birthday')) return Cake;
    return PartyPopper;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          data-testid="celebration-modal-backdrop"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 220, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl bg-zinc-900 border border-zinc-800 p-6 shadow-[0_0_40px_rgba(236,72,153,0.25)]"
            data-testid="celebration-modal"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-pink-400" />
                <h2 className="text-white font-black text-xl" style={{ fontFamily: 'Outfit' }}>Celebration Hub</h2>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-400 flex items-center justify-center transition-colors"
                data-testid="celebration-modal-close"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-zinc-400 text-sm mb-5">
              Plays right after the current track — priority-1 injection, no queue rearranging.
            </p>

            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-pink-400 animate-spin" /></div>
            ) : tracks.length === 0 ? (
              <p className="text-center text-zinc-500 text-sm py-8">Couldn't load celebrations. Try again in a moment.</p>
            ) : (
              <div className="space-y-3">
                {tracks.map((t) => {
                  const Icon = iconFor(t);
                  const busy = firingId === t.id;
                  const kind = (t?.name || '').toLowerCase().includes('birthday') ? 'birthday' : 'congrats';
                  return (
                    <button
                      key={t.id}
                      onClick={() => fire(t)}
                      disabled={!!firingId}
                      className="w-full flex items-center gap-4 p-4 rounded-2xl bg-zinc-800/60 border border-zinc-800 text-left transition-colors disabled:opacity-50 hover:bg-zinc-800"
                      data-testid={`celebration-${kind}`}
                    >
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-pink-500 shadow-[0_0_16px_rgba(236,72,153,0.5)]">
                        <Icon className="w-5 h-5 text-white" strokeWidth={2.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-base truncate" style={{ fontFamily: 'Outfit' }}>{t.name}</p>
                        <p className="text-zinc-400 text-xs truncate">
                          {(Array.isArray(t.artists) ? t.artists.join(', ') : t.artist_name) || ''}
                        </p>
                      </div>
                      {busy ? (
                        <Loader2 className="w-4 h-4 text-pink-400 animate-spin" />
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest font-mono text-pink-400">Play next</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Root layout
// ─────────────────────────────────────────────────────────────────────────
const ControlBoothPage = () => {
  const { user } = useAuth();
  const { currentTrack } = useMusic();
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const [mobilePickerOpen, setMobilePickerOpen] = useState(false);
  const playlist = usePlaylistState();
  const currentTrackId = currentTrack?.id;

  return (
    <Layout>
      <div
        className="min-h-screen bg-[#09090B] text-white content-scroll"
        data-testid="control-booth-page"
      >
        {/* ==============================================================
            MOBILE LAYOUT (< xl) — v3 spec (Feb 14 2026, view-only home)
            ┌─────────────────────────┐  ← viewport top
            │  Q1  · search + celebrate │  sticky
            ├─────────────────────────┤
            │  Q2  · guest requests    │  scrollable, ~22vh
            ├─────────────────────────┤
            │  Q3  · BIG artwork       │
            │  Q3  · title / artist    │  scrollable when needed
            │  Q3  · seek + transport  │
            ├─────────────────────────┤
            │  Q4  · collapsible list  │  collapsed by default
            ├─────────────────────────┤
            │  Q5  · playlist picker   │  fixed, replaces bottom nav
            └─────────────────────────┘
            Rationale: mid-playback taps on the tracklist have been the
            trigger for the entire class of Apple MusicKit race
            conditions (fast-path vs. crossfade prewarm collisions,
            Feb 13/14 field reports). Making the Home surface VIEW-ONLY
            for playback removes the trigger entirely — the DJ Console
            was already deprecated from the customer surface, so this
            leaves no click-to-jump path.
            ============================================================== */}
        <div className="xl:hidden flex flex-col h-[calc(100vh-3.5rem)] pb-[104px]" data-testid="mobile-booth-v3">
          {/* Q1 — Search + Celebrate */}
          <div
            className="flex items-center gap-2 p-3 border-b border-zinc-800/70 bg-[#09090B]/95 backdrop-blur z-20 flex-shrink-0"
            data-testid="mobile-q1"
          >
            <BoothSearchBar compact />
            <button
              onClick={() => setCelebrationOpen(true)}
              className="w-11 h-11 flex-shrink-0 rounded-full bg-pink-500 hover:bg-pink-400 flex items-center justify-center shadow-[0_0_20px_rgba(236,72,153,0.45)] active:scale-95 transition-transform"
              data-testid="celebration-open-fab"
              aria-label="Celebrate"
            >
              <Sparkles className="w-5 h-5 animate-pulse" />
            </button>
          </div>

          {/* Scroll container — Q2 + Q3 + Q4 scroll together */}
          <div className="flex-1 min-h-0 overflow-y-auto" data-testid="mobile-scroll">
            {/* Q2 — Guest requests (capped visual height, still scrolls with page) */}
            <div
              className="flex flex-col border-b border-zinc-800 bg-zinc-950"
              data-testid="mobile-q2-requests"
            >
              <div className="px-4 py-2 flex items-center gap-2 border-b border-zinc-800/70 flex-shrink-0">
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Live Requests</span>
              </div>
              <div className="max-h-[22vh] overflow-y-auto px-3 py-2">
                <RequestsInbox />
              </div>
            </div>

            {/* Q3 — Big centered player */}
            <div className="px-5 py-6" data-testid="mobile-q3-player">
              <MobileBigPlayer />
            </div>

            {/* Q4 — Collapsible tracklist (READ ONLY, collapsed by default) */}
            <div className="px-3 pb-4" data-testid="mobile-q4-tracks">
              <SongList
                selected={playlist.selected}
                tracks={playlist.tracks}
                loading={playlist.loading}
                tracksLoading={playlist.tracksLoading}
                todayPlaylistsCount={playlist.todayPlaylists.length}
                onQueue={playlist.queueTrack}
                currentTrackId={currentTrackId}
                activeIndex={playlist.isLive ? playlist.liveQueueIndex : null}
                isLive={playlist.isLive}
                collapsible
                defaultCollapsed
              />
            </div>
          </div>

          {/* Q5 — Horizontal playlist picker (replaces bottom nav on HOME) */}
          {playlist.todayPlaylists.length > 0 && (
            <div
              className="fixed bottom-0 left-0 right-0 border-t border-zinc-800 bg-zinc-950/98 backdrop-blur z-30 px-3 py-3"
              data-testid="mobile-picker-strip"
            >
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">
                  Playlists · {new Date().toLocaleDateString(undefined, { weekday: 'long' })}
                </span>
                <span className="text-[10px] text-zinc-600 font-mono">swipe →</span>
              </div>
              <div className="flex gap-3 overflow-x-auto snap-x scrollbar-thin pb-1 -mx-1 px-1">
                {playlist.todayPlaylists.map((pl) => {
                  const active = pl.id === playlist.selectedId;
                  return (
                    <button
                      key={pl.id}
                      onClick={() => playlist.pickPlaylist(pl.id)}
                      className={`relative flex-shrink-0 w-32 h-16 rounded-xl overflow-hidden border transition-all active:scale-95 snap-start ${
                        active ? 'border-fuchsia-500 shadow-[0_0_16px_rgba(217,70,239,0.45)]' : 'border-zinc-800'
                      }`}
                      data-testid={`mobile-picker-${pl.id}`}
                    >
                      {pl.image_url ? (
                        <img src={pl.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-900/40 to-black" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
                      <div className="absolute inset-0 p-2 flex flex-col justify-end text-left">
                        <p className="text-white font-bold text-xs leading-tight line-clamp-1" style={{ fontFamily: 'Outfit' }}>
                          {pl.name}
                        </p>
                        <p className="text-zinc-400 text-[9px] font-mono uppercase truncate">{pl._slot}</p>
                      </div>
                      {active && (
                        <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded-full bg-fuchsia-500 text-black text-[8px] font-bold tracking-wider">
                          LIVE
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ==============================================================
            DESKTOP / TABLET LAYOUT (xl+)
            ============================================================== */}
        <div className="hidden xl:grid xl:grid-cols-12 gap-4 p-4 min-h-screen">
          {/* Header — welcome + search + celebrate */}
          <div className="xl:col-span-12 flex items-center gap-3">
            <div className="min-w-0 flex-shrink-0 pr-2">
              <p className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 font-mono">Control Booth</p>
              <h1 className="text-2xl font-black tracking-tight truncate" style={{ fontFamily: 'Outfit' }}>
                {user?.name || 'Live'}
              </h1>
            </div>
            <BoothSearchBar />
            <button
              onClick={() => setCelebrationOpen(true)}
              className="h-14 px-6 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold flex items-center gap-2 shadow-[0_0_30px_rgba(236,72,153,0.5)] hover:shadow-[0_0_40px_rgba(236,72,153,0.8)] hover:scale-[1.02] active:scale-95 transition-[transform,box-shadow] flex-shrink-0"
              data-testid="celebration-open-header"
            >
              <Sparkles className="w-5 h-5 animate-pulse" />
              <span className="tracking-wider" style={{ fontFamily: 'Outfit' }}>CELEBRATE</span>
            </button>
          </div>

          {/* Middle row — Player | Song List | Inbox */}
          <div className="xl:col-span-3 min-h-[560px]">
            <MasterPlayer />
          </div>
          <div className="xl:col-span-5 min-h-[560px]">
            <SongList
              selected={playlist.selected}
              tracks={playlist.tracks}
              loading={playlist.loading}
              tracksLoading={playlist.tracksLoading}
              todayPlaylistsCount={playlist.todayPlaylists.length}
              onQueue={playlist.queueTrack}
              currentTrackId={currentTrackId}
              activeIndex={playlist.isLive ? playlist.liveQueueIndex : null}
              isLive={playlist.isLive}
            />
          </div>
          <div className="xl:col-span-4 min-h-[560px]">
            <section
              className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col h-full min-h-0"
              data-testid="control-booth-inbox"
            >
              <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(6,182,212,0.8)]" />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Live</span>
                </div>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Requests</h3>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-3">
                <RequestsInbox />
              </div>
            </section>
          </div>

          {/* Bottom — Vibe picker strip */}
          <div className="xl:col-span-12">
            <VibePicker
              todayPlaylists={playlist.todayPlaylists}
              selectedId={playlist.selectedId}
              onPick={playlist.pickPlaylist}
            />
          </div>
        </div>

        <CelebrationModal open={celebrationOpen} onClose={() => setCelebrationOpen(false)} />
      </div>
    </Layout>
  );
};

export default ControlBoothPage;
